import { useEffect, useRef, useState, type FC, type ReactNode } from "react";

interface DeferMountProps {
    children: ReactNode;
    /** Reserved height (px) for the placeholder so scroll length is preserved
     *  before the real content mounts (reduces scroll-jump on large markets). */
    minHeight?: number;
    /** How far outside the viewport to start mounting, so content is ready
     *  before it scrolls fully into view. */
    rootMargin?: string;
}

/**
 * Renders a light placeholder (reserving `minHeight`) until it scrolls near the
 * viewport, then mounts `children` once and keeps them.
 *
 * Used by the market template (FacilitySection) so a page with many facilities
 * doesn't mount every facility's unit rows — with their Rent/Reserve buttons,
 * promo dropdowns and tooltips — up front. Only facilities in or near view
 * hydrate their rows; the rest mount as the user scrolls. Falls back to
 * mounting immediately when IntersectionObserver isn't available.
 */
export const DeferMount: FC<DeferMountProps> = ({
    children,
    minHeight = 200,
    rootMargin = "400px",
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (visible) return;
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [visible, rootMargin]);

    return (
        <div ref={ref} style={visible ? undefined : { minHeight }}>
            {visible ? children : null}
        </div>
    );
};
